import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getQueryDetails } from "../../api/queries";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import assetsImg from "../../assets/assetsImg.png";
import { FaArrowLeft, FaCheckCircle, FaClock, FaEnvelopeOpenText, FaUserCheck } from "react-icons/fa";
import { toast } from "react-toastify";

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getEmployeeName = (employee = {}) =>
  `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "Unnamed staff";

const getPriorityColor = (priority) => {
  const colors = {
    Low: "bg-green-100 text-green-800",
    Medium: "bg-yellow-100 text-yellow-800",
    High: "bg-orange-100 text-orange-800",
    Critical: "bg-red-100 text-red-800",
  };
  return colors[priority] || colors.Medium;
};

const getStatusColor = (status) => {
  const colors = {
    Pending: "bg-gray-100 text-gray-800",
    Active: "bg-blue-100 text-blue-800",
    "In Progress": "bg-purple-100 text-purple-800",
    Resolved: "bg-green-100 text-green-800",
    Closed: "bg-gray-500 text-white",
  };
  return colors[status] || colors.Pending;
};

export default function QueryDetails() {
  const { queryId } = useParams();
  const [query, setQuery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadQuery = useCallback(async () => {
    if (!queryId) return;

    try {
      setIsLoading(true);
      const response = await getQueryDetails(queryId);
      setQuery(response);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load query history");
    } finally {
      setIsLoading(false);
    }
  }, [queryId]);

  useEffect(() => {
    void loadQuery();
  }, [loadQuery]);

  const assignments = useMemo(() => query?.assignments || [], [query]);
  const responses = useMemo(() => query?.responses || [], [query]);
  const statusHistory = useMemo(() => query?.statusHistory || [], [query]);
  const respondedCount = assignments.filter((item) => item.hasResponded).length;
  const readCount = assignments.filter((item) => item.isRead).length;
  const latestServedAt = assignments.reduce((latest, item) => {
    const assignedAt = item.assignedAt ? new Date(item.assignedAt).getTime() : 0;
    return assignedAt > latest ? assignedAt : latest;
  }, 0);

  if (isLoading) {
    return <FetchLoadingState message="Loading query history..." />;
  }

  if (!query) {
    return (
      <div className="dashboard-enter">
        <PageIntroNav
          breadcrumbs={[{ label: "Query Management", path: "/query-management" }, { label: "Query History" }]}
          title="Query History"
          subtitle="Unable to load this query."
          fallbackPath="/query-management/admin"
          backLabel="Back to Query Management"
        />
      </div>
    );
  }

  return (
    <div className="dashboard-enter">
      <PageIntroNav
        breadcrumbs={[
          { label: "Query Management", path: "/query-management" },
          { label: "Admin View", path: "/query-management/admin" },
          { label: "Query History" },
        ]}
        title={query.title || query.subject || "Query History"}
        subtitle="View served history, affected staff, response activity, and status movement."
        fallbackPath="/query-management/admin"
        backLabel="Back to Query Management"
      />

      <Link
        to="/query-management/admin"
        className="mb-5 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        <FaArrowLeft size={12} />
        Back to all queries
      </Link>

      <div className="mb-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-2 flex flex-wrap gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getPriorityColor(query.priority)}`}>
                {query.priority}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(query.status)}`}>
                {query.status}
              </span>
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                {{ QUERY: "Query", WARNING: "Warning", SUSPENSION: "Suspension" }[query.disciplinaryType || "QUERY"]}
              </span>
            </div>
            <p className="max-w-4xl whitespace-pre-wrap text-sm leading-6 text-gray-600">{query.description}</p>
          </div>
          <div className="text-right text-sm text-gray-500">
            <p>Created: {formatDateTime(query.createdAt)}</p>
            <p>Expected resolution: {formatDateTime(query.expectedResolutionDate)}</p>
            {query.disciplinaryType === "SUSPENSION" ? <p className="font-semibold text-rose-600">Effective: {formatDateTime(query.effectiveStartDate)} – {formatDateTime(query.effectiveEndDate)}</p> : null}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <FaUserCheck className="mb-2 text-[#1234CD]" />
            <p className="text-2xl font-semibold text-gray-900">{assignments.length}</p>
            <p className="text-xs text-gray-500">Affected staff</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4">
            <FaEnvelopeOpenText className="mb-2 text-[#1234CD]" />
            <p className="text-2xl font-semibold text-gray-900">{readCount}</p>
            <p className="text-xs text-gray-500">Read by staff</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4">
            <FaCheckCircle className="mb-2 text-[#1234CD]" />
            <p className="text-2xl font-semibold text-gray-900">{respondedCount}</p>
            <p className="text-xs text-gray-500">Responses submitted</p>
          </div>
          <div className="rounded-lg bg-gray-50 p-4">
            <FaClock className="mb-2 text-[#1234CD]" />
            <p className="text-sm font-semibold text-gray-900">{formatDateTime(latestServedAt)}</p>
            <p className="text-xs text-gray-500">Last served time</p>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <p className="text-lg font-semibold text-gray-900">Affected staff and served history</p>
          <p className="text-sm text-gray-500">Every staff member this query was served to, with read and response time.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-sm text-gray-600">
                <th className="px-5 py-3 font-semibold">Staff</th>
                <th className="px-5 py-3 font-semibold">Department</th>
                <th className="px-5 py-3 font-semibold">Role</th>
                <th className="px-5 py-3 font-semibold">Served time</th>
                <th className="px-5 py-3 font-semibold">Read time</th>
                <th className="px-5 py-3 font-semibold">Response time</th>
                <th className="px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-5 py-8 text-center text-sm text-gray-500">
                    This query has not been served to any staff yet.
                  </td>
                </tr>
              ) : (
                assignments.map((assignment) => {
                  const employee = assignment.employee || {};
                  return (
                    <tr key={assignment.id} className="border-b border-gray-100 text-sm text-gray-700">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={employee.profilePhotoUrl || assetsImg}
                            alt={getEmployeeName(employee)}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-semibold text-gray-900">{getEmployeeName(employee)}</p>
                            <p className="text-xs text-gray-500">
                              {employee.officialEmail || employee.personalEmail || "-"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">{employee.department?.name || "-"}</td>
                      <td className="px-5 py-4">{employee.role?.name || "-"}</td>
                      <td className="px-5 py-4">{formatDateTime(assignment.assignedAt)}</td>
                      <td className="px-5 py-4">{formatDateTime(assignment.readAt)}</td>
                      <td className="px-5 py-4">{formatDateTime(assignment.respondedAt)}</td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${assignment.hasResponded ? "bg-green-100 text-green-700" : assignment.isRead ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                          {assignment.hasResponded ? "Responded" : assignment.isRead ? "Read" : "Served"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="mb-4 text-lg font-semibold text-gray-900">Staff response history</p>
          {responses.length === 0 ? (
            <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">No staff response has been submitted yet.</p>
          ) : (
            <div className="space-y-3">
              {responses.map((response) => (
                <div key={response.id} className="rounded-lg border border-gray-100 p-4">
                  <div className="mb-2 flex flex-wrap justify-between gap-2">
                    <p className="font-semibold text-gray-900">{getEmployeeName(response.employee)}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(response.submittedAt)}</p>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-gray-600">{response.responseText}</p>
                  {response.attachmentUrl ? (
                    <a className="mt-2 inline-block text-sm font-semibold text-[#1234CD]" href={response.attachmentUrl} target="_blank" rel="noreferrer">
                      View attachment
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="mb-4 text-lg font-semibold text-gray-900">Status history</p>
          {statusHistory.length === 0 ? (
            <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">No status movement has been recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {statusHistory.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-100 p-4">
                  <div className="mb-1 flex flex-wrap justify-between gap-2">
                    <p className="font-semibold text-gray-900">
                      {item.oldStatus || "Created"} to {item.newStatus}
                    </p>
                    <p className="text-xs text-gray-500">{formatDateTime(item.createdAt)}</p>
                  </div>
                  <p className="text-sm text-gray-600">{item.reason || "No note captured."}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
